/**
 * useToastStore — the popups: errors, warnings, and "done" confirmations.
 *
 * A screen's error used to be a red strip somewhere in its layout: under a
 * form, beside a button, below the fold on a phone. Successes were worse —
 * mostly nothing at all: a form closed, and the operator was left to guess
 * whether the product, customer or payment had been saved. Every outcome now
 * appears in one place, the top of the window, in the same frosted card with
 * a close button, coloured by what happened.
 *
 * How long each stays is by tone:
 *   error   — until closed, or until the screen that raised it clears its
 *             error. An operator looking at the customer should still find it.
 *   warning — longer than a success; it asks for a second look.
 *   success / info — a few seconds. Confirmation, not a task.
 */
import { create } from 'zustand';

export type ToastTone = 'error' | 'warning' | 'success' | 'info';

export interface Toast {
  id: number;
  message: string;
  tone: ToastTone;
  /** Who raised it. A source holds one popup at a time; a new error replaces it. */
  source: string | null;
  /** Run when the operator closes it — the screen clears its error, so the
   *  same error raised again (a retry that fails the same way) shows again. */
  onClose?: () => void;
}

interface PushOptions {
  tone?: ToastTone;
  source?: string | null;
  onClose?: () => void;
}

interface ToastState {
  toasts: Toast[];
  /** Shows `message`, returning its id. The same message is never shown twice at once. */
  push: (message: string, options?: PushOptions) => number;
  dismiss: (id: number) => void;
  /** Closes the popup a source raised, if it is still open. */
  dismissSource: (source: string) => void;
}

/** More than this and the oldest go: a stack of stale popups hides the page. */
const MAX_TOASTS = 3;

/** Milliseconds before a popup closes itself; null = stays until closed. */
export const TOAST_LIFETIME: Record<ToastTone, number | null> = {
  error: null,
  warning: 8000,
  success: 3500,
  info: 5000,
};

let nextId = 1;
const timers = new Map<number, ReturnType<typeof setTimeout>>();

export const useToastStore = create<ToastState>((set, get) => ({
  toasts: [],
  push: (message, { tone = 'error', source = null, onClose } = {}) => {
    const text = message.trim();
    const same = get().toasts.find((t) => t.message === text && t.tone === tone);
    if (same) return same.id;
    const id = nextId++;
    set((s) => {
      const kept = source ? s.toasts.filter((t) => t.source !== source) : s.toasts;
      const next = [...kept, { id, message: text, tone, source, onClose }];
      // Past the cap, successes and infos give way before errors do.
      while (next.length > MAX_TOASTS) {
        const i = next.findIndex((t) => t.tone === 'success' || t.tone === 'info');
        next.splice(i >= 0 ? i : 0, 1);
      }
      return { toasts: next };
    });
    const life = TOAST_LIFETIME[tone];
    if (life !== null) timers.set(id, setTimeout(() => get().dismiss(id), life));
    return id;
  },
  dismiss: (id) => {
    const timer = timers.get(id);
    if (timer) { clearTimeout(timer); timers.delete(id); }
    const toast = get().toasts.find((t) => t.id === id);
    if (!toast) return;
    set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
    toast.onClose?.();
  },
  dismissSource: (source) => set((s) => ({ toasts: s.toasts.filter((t) => t.source !== source) })),
}));

/*
 * `source` names what raised the popup (a form, an action). A new popup from
 * the same source replaces the old one — so when a form that just said
 * "Enter the supplier's name" then saves, the confirmation takes the error's
 * place instead of leaving a stale error on screen.
 */

/** An error popup from anywhere — an event handler, a catch block. Stays until closed. */
export function notifyError(message: string, source?: string): void {
  useToastStore.getState().push(message, { tone: 'error', source });
}

/** "Done" — the product was saved, the payment recorded. Closes itself. */
export function notifySuccess(message: string, source?: string): void {
  useToastStore.getState().push(message, { tone: 'success', source });
}

/** Went through, but something needs a second look. */
export function notifyWarning(message: string, source?: string): void {
  useToastStore.getState().push(message, { tone: 'warning', source });
}

export function notifyInfo(message: string, source?: string): void {
  useToastStore.getState().push(message, { tone: 'info', source });
}
